// Stillhouse view — Security & compliance (session policy + audit log).
//
// The policy tab is live: it reads GET /settings/session-policy, writes each
// toggle back through PUT (settings.manage), and reports what the save reached
// on the fleet. It renders exactly the two settings the API can enforce as
// Apollo per-client permission bits — the other 16 toggles the mockup shipped
// had no writer, no reader and no host-side mechanism, so they were removed
// rather than made to look functional (see lib/sessionPolicy.ts).
//
// The audit tab is live: it reads GET /api/audit (audit.read), verifies the
// hash chain through GET /api/audit/verify, and the CSV button downloads
// GET /api/audit/export.csv. The chip and the retention line report what the
// API actually reports — the chain's verified state and the server's configured
// retention window — rather than a fixed claim.
import React, { useCallback, useEffect, useState } from "react";
import { C, F, Btn } from "../brand/apo";
import { Card, Chip, Toggle, Tabs, PageHeader, EmptyState } from "../brand/sh";
import { api, type AuditChainStatus, type AuditEvent, type SessionPolicy, type SessionPolicyPush } from "../api";
import { useAuth } from "../auth";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);
function Row({ title, sub, children, last }: { title: React.ReactNode; sub?: React.ReactNode; children?: React.ReactNode; last?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "15px 18px", borderBottom: last ? "none" : `1px solid ${C.line}` }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, color: C.fgDark, fontWeight: 500 }}>{title}</div>
        {sub && <div style={{ fontSize: 12.5, color: C.fgMute, marginTop: 3, lineHeight: 1.45 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

const AUDIT_PAGE_SIZE = 100;

// 395 days is 13 months; anything else is reported in days rather than rounded
// into a month count the operator never configured.
function retentionLabel(days: number): string {
  if (days <= 0) return "RETAINED INDEFINITELY";
  if (days % 30 === 0 || days === 395) return `RETAINED ${Math.round(days / 30.44)} MONTHS`;
  return `RETAINED ${days} DAYS`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 19)}Z`;
}

// The chip is the chain's verified state, not a marketing claim: hash-chained
// and verified, hash-chained but never checked, or broken.
function chainChip(chain: AuditChainStatus | null, checking: boolean): { label: string; color: string } {
  if (checking || !chain) return { label: "Verifying chain…", color: C.fgMute };
  if (chain.truncatedTail) return { label: `${chain.missingTail} record(s) deleted from the head`, color: C.rose };
  if (!chain.ok) return { label: `Chain broken at #${chain.brokenAtSeq ?? "?"}`, color: C.rose };
  if (chain.records === 0) return { label: "Hash-chained", color: C.fgMute };
  return { label: `Hash-chained · ${chain.records} verified`, color: C.glow };
}

function AuditTable({ events }: { events: AuditEvent[] }) {
  const cols = "150px 200px 190px 1fr 60px";
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: cols, padding: "11px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.12em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
        <span>TIME (UTC)</span>
        <span>EVENT</span>
        <span>ACTOR</span>
        <span>TARGET / DETAIL</span>
        <span style={{ textAlign: "right" }}>SEQ</span>
      </div>
      {events.map((e, i) => (
        <div key={e.id} style={{ display: "grid", gridTemplateColumns: cols, gap: 8, padding: "11px 18px", borderBottom: i < events.length - 1 ? `1px solid ${C.line}` : "none", alignItems: "baseline", fontSize: 13, color: C.fgDark, minWidth: 0 }}>
          <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.fgMute }}>{fmtTime(e.createdAt)}</span>
          <span style={{ fontFamily: F.mono, fontSize: 12, color: e.event.endsWith(".failed") || e.event.endsWith(".rejected") || e.event.endsWith(".blocked") ? C.amber : C.fgDark, overflow: "hidden", textOverflow: "ellipsis" }}>{e.event}</span>
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
            {e.actorLabel ?? (e.actorType === "SYSTEM" ? "system" : "—")}
            {e.actorIp && <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint }}> · {e.actorIp}</span>}
          </span>
          <span style={{ color: C.fgMute, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
            {e.detail ?? ""}
            {e.target && <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint }}>{e.detail ? " · " : ""}{e.target}</span>}
          </span>
          <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint, textAlign: "right" }}>{e.seq}</span>
        </div>
      ))}
    </div>
  );
}

// Each row names the Apollo permission bits it writes, so what the toggle does
// is legible from the UI rather than only from the code.
const POLICY_ROWS: { key: keyof SessionPolicy; title: string; sub: string }[] = [
  {
    key: "permClipboard",
    title: "Clipboard",
    sub: "Copy and paste between the client and the host — Apollo clipboard_set + clipboard_read",
  },
  {
    key: "permFileTransfer",
    title: "File transfer",
    sub: "Send and receive files during a session — Apollo file_upload + file_dwnload",
  },
];

export function Security() {
  const { can } = useAuth();
  const canReadAudit = can("audit.read");
  const canManagePolicy = can("settings.manage");

  const [tab, setTab] = useState("policies");
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);
  const [chain, setChain] = useState<AuditChainStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [policy, setPolicy] = useState<SessionPolicy | null>(null);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [saving, setSaving] = useState<keyof SessionPolicy | null>(null);
  const [push, setPush] = useState<SessionPolicyPush | null>(null);

  const loadAudit = useCallback(async () => {
    if (!canReadAudit) return;
    setLoading(true);
    setError(null);
    try {
      const [page, verification] = await Promise.all([api.getAudit({ limit: AUDIT_PAGE_SIZE }), api.verifyAudit()]);
      setEvents(page.events);
      setTotal(page.total);
      setRetentionDays(page.retentionDays);
      setChain(verification);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [canReadAudit]);

  // The tab count is a real number, so the log loads with the view rather than
  // when the tab is first opened.
  useEffect(() => {
    void loadAudit();
  }, [loadAudit]);

  const loadPolicy = useCallback(async () => {
    if (!canManagePolicy) return;
    setPolicyError(null);
    try {
      setPolicy((await api.getSessionPolicy()).policy);
    } catch (err) {
      setPolicyError((err as Error).message);
    }
  }, [canManagePolicy]);

  useEffect(() => {
    void loadPolicy();
  }, [loadPolicy]);

  // Optimistic, then reconciled with what the server stored — a rejected save
  // must not leave the switch showing a setting the fleet is not running.
  const setPolicyField = async (key: keyof SessionPolicy, value: boolean) => {
    if (!policy || saving) return;
    const previous = policy;
    setPolicy({ ...policy, [key]: value });
    setSaving(key);
    setPolicyError(null);
    try {
      const saved = await api.updateSessionPolicy({ [key]: value });
      setPolicy(saved.policy);
      setPush(saved.push);
    } catch (err) {
      setPolicy(previous);
      setPolicyError((err as Error).message);
    } finally {
      setSaving(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const csv = await api.exportAuditCsv();
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `stillhouse-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const chip = chainChip(chain, loading);
  return (
    <React.Fragment>
      <PageHeader
        crumb="GOVERNANCE › SECURITY"
        title="Security & compliance"
        sub="Session defaults enforced on every paired client, and a hash-chained audit trail of every privileged action across the fleet."
      >
        {canReadAudit && (
          <Btn ghost onClick={exportCsv}>
            {exporting ? "Exporting…" : "Export audit (CSV)"}
          </Btn>
        )}
      </PageHeader>
      <div style={{ padding: "0 32px", borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: "policies", label: "Session policy" },
            { id: "audit", label: "Audit log", count: total ?? undefined },
          ]}
        />
      </div>
      <Scroll>
        {tab === "policies" ? (
          <Card
            pad={false}
            title="Session defaults"
            tag={<Chip color={C.fgMute}>ENFORCED ON PAIRED CLIENTS</Chip>}
            action={
              canManagePolicy ? (
                <Btn ghost onClick={loadPolicy}>
                  Refresh
                </Btn>
              ) : undefined
            }
          >
            {!canManagePolicy ? (
              <EmptyState title="Session policy restricted" sub="REQUIRES THE SETTINGS.MANAGE PERMISSION" />
            ) : !policy ? (
              <EmptyState
                title={policyError ? "Could not load the session policy" : "Loading session policy…"}
                sub={policyError ? policyError.toUpperCase() : "GET /API/SETTINGS/SESSION-POLICY"}
              />
            ) : (
              <React.Fragment>
                {policyError && (
                  <div style={{ padding: "11px 18px", borderBottom: `1px solid ${C.line}`, background: "rgba(255,111,143,0.08)", color: C.rose, fontSize: 13 }}>
                    {policyError} — the switch was put back to the stored value.
                  </div>
                )}
                {POLICY_ROWS.map((row) => (
                  <Row key={row.key} title={row.title} sub={row.sub}>
                    <Toggle
                      on={policy[row.key]}
                      disabled={saving !== null}
                      onChange={(next) => void setPolicyField(row.key, next)}
                    />
                  </Row>
                ))}
                {/* What the setting actually does to the fleet, and what the
                    last save reached — an unreachable host is named rather
                    than assumed compliant. */}
                <div style={{ padding: "13px 18px", fontSize: 12.5, color: C.fgMute, lineHeight: 1.5 }}>
                  Applied to every paired client on every enrolled host, and re-pushed periodically so a host that was
                  offline catches up. This is an org-wide default: it overwrites per-client clipboard and file-transfer
                  permissions set on a host directly.
                  {push && (
                    <div style={{ marginTop: 7, fontFamily: F.mono, fontSize: 11, letterSpacing: "0.06em", color: push.failures.length ? C.amber : C.fgFaint }}>
                      LAST SAVE: {push.applied}/{push.hosts} HOSTS · {push.clientsUpdated} CLIENT PERMISSION(S) CHANGED
                      {push.failures.length > 0 && ` · NOT REACHED: ${push.failures.map((f) => f.hostname).join(", ")}`}
                    </div>
                  )}
                </div>
              </React.Fragment>
            )}
          </Card>
        ) : (
          <Card
            pad={false}
            tag={<Chip color={chip.color}>{chip.label}</Chip>}
            title="Audit log"
            action={
              <span style={{ display: "flex", alignItems: "center", gap: 14 }}>
                {canReadAudit && (
                  <Btn ghost onClick={loadAudit}>
                    {loading ? "Loading…" : "Refresh"}
                  </Btn>
                )}
                <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint, letterSpacing: "0.1em" }}>
                  {retentionLabel(retentionDays ?? 0)}
                </span>
              </span>
            }
          >
            {!canReadAudit ? (
              <EmptyState title="Audit log restricted" sub="REQUIRES THE AUDIT.READ PERMISSION" />
            ) : error ? (
              <EmptyState title="Could not load the audit log" sub={error.toUpperCase()} />
            ) : events.length === 0 ? (
              <EmptyState title={loading ? "Loading audit events…" : "No audit events yet"} sub="FLEET ACTIONS ARE RECORDED HERE" />
            ) : (
              <React.Fragment>
                {chain && chain.truncatedTail && (
                  <div style={{ padding: "11px 18px", borderBottom: `1px solid ${C.line}`, background: "rgba(255,111,143,0.08)", color: C.rose, fontSize: 13 }}>
                    {chain.missingTail} record(s) recorded up to #{chain.anchorSeq} are gone — the log stops at #{chain.lastSeq}. Records were
                    deleted from the newest end; the retention prune never does this.
                  </div>
                )}
                {chain && !chain.ok && !chain.truncatedTail && (
                  <div style={{ padding: "11px 18px", borderBottom: `1px solid ${C.line}`, background: "rgba(255,111,143,0.08)", color: C.rose, fontSize: 13 }}>
                    Chain verification failed: {chain.reason ?? "unknown"}
                    {chain.brokenAtSeq != null && <> — records after #{chain.brokenAtSeq} cannot be trusted</>}.
                  </div>
                )}
                {chain?.ok && chain.truncatedPrefix && (
                  <div style={{ padding: "11px 18px", borderBottom: `1px solid ${C.line}`, color: C.fgMute, fontSize: 12.5 }}>
                    Chain verified from #{chain.firstSeq}. Earlier records are past the retention window.
                  </div>
                )}
                <AuditTable events={events} />
                {total !== null && total > events.length && (
                  <div style={{ padding: "11px 18px", fontFamily: F.mono, fontSize: 11, color: C.fgFaint, letterSpacing: "0.08em" }}>
                    SHOWING {events.length} OF {total} — EXPORT CSV FOR THE FULL RANGE
                  </div>
                )}
              </React.Fragment>
            )}
          </Card>
        )}
      </Scroll>
    </React.Fragment>
  );
}
